import React from 'react'
import * as PIXI from 'pixi.js';
import { scaleLinear } from 'd3-scale';

function cubicInOut(t) {
  return t < 0.5
    ? 4.0 * t * t * t
    : 0.5 * Math.pow(2.0 * t - 2.0, 3.0) + 1.0;
}

export default class PixiCanvas extends React.Component {
  componentDidMount() {
    this._app = new PIXI.Application({
      width: this.props.width,
      height: this.props.height,
      transparent: true,
      antialias: true, // trying to smooth circles
    });

    PIXI.settings.SCALE_MODE = PIXI.SCALE_MODES.LINEAR; // trying to smooth circles

    this._doneAnimating = false

    this._container.appendChild(this._app.view);

    // layer our stage, non extreme points in the back
    this._particleContainerNonExtreme = new PIXI.particles.ParticleContainer(11000, {
      scale: true,
      position: true,
      rotation: true,
      uvs: true,
      alpha: true
    })
    this._app.stage.addChild(this._particleContainerNonExtreme);

    // then draw the lines
    this._lineContainer = new PIXI.Container()
    this._app.stage.addChild(this._lineContainer);

    // then draw the extremem points
    this._particleContainerExtreme = new PIXI.particles.ParticleContainer(11000, {
      scale: true,
      position: true,
      rotation: true,
      uvs: true,
      alpha: true
    })
    this._app.stage.addChild(this._particleContainerExtreme);

    this._tip = {
      scaleUp: false,
      frame: 0,
      frames: 12,
      scale: 1,
      line: null,
    };
    this._elems = {
      frame: 36,
      frames: 36,
    };

    // plot the points
    this._resetDots(this.props.data);
  }

  componentWillReceiveProps(nextProps) {
    // if new data replot
    if (nextProps.data.length !== this.props.data.length) {
      this._resetDots(nextProps.data);
    }
    // resize if necessary
    if (nextProps.width !== this.props.width || nextProps.height !== this.props.height) {
      this._app.renderer.resize(nextProps.width, nextProps.height);
    }
  }

  _resetDots(data) {
    const rScale = scaleLinear()
                    .domain([320, 800])
                    .range([0.5, 1])
                    .clamp(true);

    this._tip.scale = rScale(this.props.width);

    const circle = new PIXI.Graphics();
    circle.beginFill(0xffffff);
    circle.drawCircle(0, 0, 12);
    circle.endFill();
    circle.cacheAsBitmap = true;
    const circleTexture = this._app.renderer.generateTexture(circle);

    const line = new PIXI.Graphics();
    line.lineStyle(1, 0x808080, 0.1 * this._tip.scale);
    line.moveTo(0, 0);
    line.lineTo(0, 2);
    line.closePath();
    const lineTexture = this._app.renderer.generateTexture(line);

    const darkLine = new PIXI.Graphics();
    darkLine.lineStyle(1, 0x808080, 0.25 * this._tip.scale);
    darkLine.moveTo(0, 0);
    darkLine.lineTo(0, 2);
    darkLine.closePath();
    const darkLineTexture = this._app.renderer.generateTexture(darkLine);
    this._tip.line = new PIXI.Sprite(darkLineTexture);
    this._tip.line.anchor.set(0.5, 0);
    this._tip.line.x = 0;
    this._tip.line.y = 0;
    this._app.stage.addChild(this._tip.line);

    data.forEach(datum => {
      datum.highlighted = true;
      // circle stroke omitted for now...
      const sprite = new PIXI.Sprite(circleTexture);
      sprite.tint = datum.fill
      sprite.alpha = datum.circleOpacity;
      sprite.anchor.set(0.5);
      sprite.scale.set(this._tip.scale / 4);
      sprite.x = datum.x;
      sprite.y = datum.y1;
      datum.sprite = sprite;
      // add sprite selectively to extreme or non container (bottom or top container)
      this[
        datum.extreme ? '_particleContainerExtreme' : '_particleContainerNonExtreme'
      ].addChild(sprite);

      // add line for extreme points
      if (datum.extreme) {
        const lineSprite = new PIXI.Sprite(lineTexture);
        lineSprite.anchor.set(0.5, 0);
        lineSprite.x = datum.x;
        lineSprite.y = datum.y1;
        lineSprite.scale.y = (datum.y2 - datum.y1) / 2;
        datum.line = lineSprite;
        this._lineContainer.addChild(lineSprite);
      }
    })
  }

  _setHighlight(xpos) {
    this.props.data.map((d, i) => {
      if (d.date.getTime() === this.props.date) {
        d.highlighted = true;
        d.circleOpacity = d.extreme ? 1 : 0.6;
      } else {
        d.highlighted = false;
        d.circleOpacity = d.extreme ? 0.8 : 0.2;
      }
      return d;
    });
  }

  _unsetHighlight() {
    this.props.data.map((d, i) => {
      d.highlighted = true;
      d.circleOpacity = d.extreme ? 0.8 : 0.2;
      return d;
    });
  }

  _mouseMove = (event) => {
    if (!this._doneAnimating) {
      return
    }
    const x = event.clientX || event.touches[0].clientX;
    const y = event.clientY || event.touches[0].clientY;
    if (x === undefined || y === undefined) {
      return;
    }
    const xy = [x, y];
    const containerXY = this._container.getBoundingClientRect();
    xy[0] -= containerXY.left;
    xy[1] -= containerXY.top;
    //
    this._tip.scaleUp = true;
    this._tip.frame = 0;
    //
    this._tip.line.x = xy[0];
    this._tip.line.scale.y = (this.props.height / 2) - 8;
    //
    this._setHighlight(xy[0]);
    //
    this.props.onHover(xy);
  }

  _mouseLeave = () => {
    this._tip.scaleUp = false;
    // this._tip.frame = 0;
    this._elems.frame = 7;
    //
    this._tip.line.scale.y = 0;
    this._unsetHighlight();
    this.props.onOut();
  }

  animate(appState, frame, framesPerDatum) {
    let numDone = 0;
    // update everything, perhaps inefficient if no updates needed?
    this.props.data.forEach((datum, i) => {
      let y2 = datum.y1
      const startFrame = ~~(i / 200)
      const frameRange = [startFrame, startFrame + framesPerDatum];
      if (frame > frameRange[0] && frame < frameRange[1]) {
        const t = cubicInOut((frame - frameRange[0]) / framesPerDatum)
        y2 = datum.y1 * (1 - t) + datum.y2 * t
      } else if (frame >= frameRange[1]) {
        y2 = datum.y2
      }
      if (frame >= frameRange[1]) {
        numDone ++
      }
      const visible = appState.divisions[datum.group]
      if (datum.highlighted && this._tip.scaleUp) {
        datum.sprite.scale.set(this._tip.scale * (0.125 + 0.125 * cubicInOut(this._tip.frame / this._tip.frames)));
      } else {
        datum.sprite.scale.set(this._tip.scale / 8);
      }
      datum.sprite.alpha = (
        visible ? (
          datum.highlighted ? datum.circleOpacity : 0.2
        ) : 0
      ) * cubicInOut(this._elems.frame / this._elems.frames);
      datum.sprite.x = datum.x
      datum.sprite.y = y2
      if (datum.extreme) {
        datum.line.alpha = (
          visible ? (
            datum.highlighted ? 1 : 0.2
          ) : 0
        ) * cubicInOut(this._elems.frame / this._elems.frames);
        datum.line.x = datum.x
        datum.line.y = datum.y1
        datum.line.scale.y = (y2 - datum.y1) / 2
      }
    })
    if (this._tip.frame < this._tip.frames) {
      this._tip.frame++;
    }
    if (this._elems.frame < this._elems.frames) {
      this._elems.frame++;
    }
    this._doneAnimating = this.props.data.length > 0 && numDone === this.props.data.length
  }

  render () {
    return (
      <div
        className='pixiCanvas'
        width={this.props.width}
        height={this.props.height}
        onMouseMove={this._mouseMove}
        onTouchStart={this._mouseMove}
        onTouchMove={this._mouseMove}        
        onMouseLeave={this._mouseLeave}
        onTouchEnd={this._mouseLeave}
        onTouchCancel={this._mouseLeave}
        ref={c => (this._container = c)}
      />
    )
  }
}


// WEBPACK FOOTER //
// ./src/PixiCanvas.js